import React from 'react';
import CourseThumbnailSection from '../course/CourseThumbnailSection';
import {
  Users,
  Play,
  Clock,
  CheckCircle,
  PlayCircle,
  CreditCard,
  Layers
} from 'lucide-react';

const ExploreCoursesCard = ({
  course,
  onViewCourse,
  onSubscribe,
  formatTotalDuration,
  getDurationTextFromDays
}) => {

  return (
    <div className="relative group bg-white rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col">
      <CourseThumbnailSection course={course} onClick={() => onViewCourse(course.id)} />
        {/* Price Tag - Top Right */}
        {course.is_free ? (
          <div className="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-lg font-black text-sm shadow-lg">
            مجاني
          </div>
        ) : (
          <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
            {course.discount_price && course.discount_price > 0 && course.discount_end_date && new Date(course.discount_end_date) > new Date() ? (
              <>
                <div className="bg-red-500 text-white px-3 py-1 rounded-lg font-black text-sm shadow-lg">
                  {course.discount_price} جنية/{getDurationTextFromDays(course.default_duration_days)}
                </div>
                <div className="bg-gray-600 text-white px-2 py-0.5 rounded line-through text-xs font-bold shadow-lg">
                  {course.price} جنية
                </div>
              </>
            ) : (
              <div className="bg-primary-500 text-white px-3 py-1 rounded-lg font-black text-sm shadow-lg">
                {course.price} جنية/{getDurationTextFromDays(course.default_duration_days)}
              </div>
            )}
          </div>
        )}

        {/* Duration Tag - Top Left */}
        <div className="absolute top-4 left-4 bg-gray-900/80 text-white px-3 py-1 rounded-lg font-black text-sm shadow-lg flex items-center gap-1.5">
          <Clock size={14} />
          {formatTotalDuration(course)}
        </div>

        {/* Floating Status Badge */}
        {course.is_subscribed && (
          <div className="absolute bottom-4 left-4 bg-green-500 text-white px-4 py-1.5 rounded-xl font-black text-xs shadow-lg flex items-center gap-1.5 animate-in slide-in-from-left duration-500">
            <CheckCircle size={14} /> تم الاشتراك
          </div>
        )}


      {/* Content Body */}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3">
          <span className="bg-primary-50 text-primary-600 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded">
            {course.subject_name}
          </span>
          <span className="bg-gray-100 text-gray-500 text-[10px] font-black px-2 py-1 rounded">
            {course.grade_name}
          </span>
        </div>

        <h3 className="text-xl font-black text-gray-900 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {course.title}
        </h3>
        <p className="text-sm text-gray-500 line-clamp-2 mb-6 leading-relaxed flex-1">
          {course.description || 'تصفح محتوى هذا الكورس والدروس المتاحة لبدء التعلم الآن.'}
        </p>

        {/* Teacher Info */}
        <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-2xl border border-gray-100">
          <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center text-white font-black shadow-lg shadow-primary-100">
            {course.teacher_name?.[0] || <Users size={20} />}
          </div>
          <div>
            <p className="text-xs text-gray-400 font-bold uppercase">المدرس</p>
            <p className="text-sm font-black text-gray-800">أ / {course.teacher_name}</p>
          </div>
        </div>

        {/* Statistics Row */}
        <div className="grid grid-cols-4 gap-2 mb-6 text-xs font-bold text-gray-400">
          <div className="flex flex-col items-center gap-1 p-2 bg-gray-50/50 rounded-xl">
            <Users size={16} className="text-blue-500" />
            <span>{course.student_count || 0} طالب</span>
          </div>
          <div className="flex flex-col items-center gap-1 p-2 bg-gray-50/50 rounded-xl">
            <Play size={16} className="text-primary-500" />
            <span>{course.courseViews ?? course.video_views ?? 0} مشاهدة</span>
          </div>
          <div className="flex flex-col items-center gap-1 p-2 bg-gray-50/50 rounded-xl">
            <Clock size={16} className="text-blue-600" />
            <span>{formatTotalDuration(course)}</span>
          </div>
          <div className="flex flex-col items-center gap-1 p-2 bg-gray-50/50 rounded-xl">
            <Layers size={16} className="text-purple-500" />
            <span>{course.lesson_count || 0} درس</span>
          </div>
        </div>

        {/* Actions Section */}
        <div className="flex gap-2">
          {course.is_subscribed ? (
            <button
              onClick={() => onViewCourse(course.id)}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4 rounded-2xl font-black transition-all flex items-center justify-center gap-2 shadow-xl shadow-green-100 transform active:scale-95"
            >
              <Play size={18} fill="currentColor" />
              مشاهدة الدروس
            </button>
          ) : (
            <>
              <button
                onClick={() => onSubscribe(course.id)}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-4 rounded-2xl font-black transition-all flex items-center justify-center gap-2 shadow-xl shadow-primary-100 transform active:scale-95"
              >
                <CreditCard size={18} />
                اشترك الآن
              </button>
              <button
                onClick={() => onViewCourse(course.id)}
                className="p-4 border border-gray-200 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-2xl transition-all shadow-sm"
                title="معاينة محتوى الكورس"
              >
                <PlayCircle size={22} />
              </button>
            </>
          )}
        </div>

        {/* Footer Status */}
        {course.is_subscribed && (
          <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-center gap-2 text-[10px] font-black text-green-600 uppercase tracking-widest">
            <CheckCircle size={12} /> متاح لك الآن
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreCoursesCard;